// src/pages/Residential/Optional/SuicideDetail/EditSuicideDetail.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";

import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import SuicideDetailForm from "./SuicideDetailForm";

import {
    fetchSuicideDetail,
    updateSuicideDetail,
} from "../../../../store/slices/suicideDetailsSlice";

/* ===================== Snake → Camel ===================== */
const mapToCamelCase = (data) => ({
    caseId: data.case_id,
    patientId: data.patient_id,

    clientName: data.client_name,
    completedBy: data.completed_by,
    date: data.date ? data.date.split('T')[0] : "",

    q1: data.q1,
    q2: data.q2,
    q3: data.q3,
    q4: data.q4,

    q5: data.q5,
    q5_attempts: data.q5_attempts,
    q5_lastAttempt: data.q5_last_attempt,
    q5_changes: data.q5_changes,

    q6: data.q6,
    q6_events: data.q6_events || [],
    q6_other: data.q6_other,

    q7: data.q7,
    q7_frequency: data.q7_frequency,
    q7_duration: data.q7_duration,
    q7_intensitySevere: data.q7_intensity_severe,
    q7_intensityWeek: data.q7_intensity_week,

    q8: data.q8,
    q8_method: data.q8_method,
    q8_access: data.q8_access,
    q8_location: data.q8_location,
    q8_preparations: data.q8_preparations,
    q8_likelihood: data.q8_likelihood,

    q9_stopped: data.q9_stopped,

    q10: data.q10,
    q10_people: data.q10_people,
    q10_contact: data.q10_contact,

    q11_pastHelp: data.q11_past_help,

    presentation: data.presentation || [],
    presentation_other: data.presentation_other,

    workerRiskLevel: data.worker_risk_level,
});

/* ===================== Camel → Snake ===================== */
const mapToSnakeCase = (data) => ({
    case_id: data.caseId,
    patient_id: data.patientId,

    client_name: data.clientName,
    completed_by: data.completedBy,
    date: data.date,

    q1: data.q1,
    q2: data.q2,
    q3: data.q3,
    q4: data.q4,

    q5: data.q5,
    q5_attempts: data.q5_attempts,
    q5_last_attempt: data.q5_lastAttempt,
    q5_changes: data.q5_changes,

    q6: data.q6,
    q6_events: data.q6_events,
    q6_other: data.q6_other,

    q7: data.q7,
    q7_frequency: data.q7_frequency,
    q7_duration: data.q7_duration,
    q7_intensity_severe: data.q7_intensitySevere,
    q7_intensity_week: data.q7_intensityWeek,

    q8: data.q8,
    q8_method: data.q8_method,
    q8_access: data.q8_access,
    q8_location: data.q8_location,
    q8_preparations: data.q8_preparations,
    q8_likelihood: data.q8_likelihood,

    q9_stopped: data.q9_stopped,

    q10: data.q10,
    q10_people: data.q10_people,
    q10_contact: data.q10_contact,

    q11_past_help: data.q11_pastHelp,

    presentation: data.presentation,
    presentation_other: data.presentation_other,

    worker_risk_level: data.workerRiskLevel,
});

const EditSuicideDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState(null);

    /* ===================== FETCH ===================== */
    useEffect(() => {
        const loadData = async () => {
            try {
                const response = await dispatch(fetchSuicideDetail(id)).unwrap();
                setFormData(mapToCamelCase(response));
            } catch (error) {
                Swal.fire("Error", "Failed to load assessment", "error");
            }
        };
        loadData();
    }, [id, dispatch]);

    /* ===================== CHANGE HANDLER ===================== */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            if (name === "q6_events" || name === "presentation") {
                setFormData((prev) => ({
                    ...prev,
                    [name]: checked
                        ? [...prev[name], value]
                        : prev[name].filter((v) => v !== value),
                }));
            }
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    /* ===================== SUBMIT ===================== */
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Create a copy of formData to modify
            const formDataToSend = { ...formData };

            // Define the conditional logic for each question
            // If parent is "no", set dependent fields to null

            // Q5 logic
            if (formDataToSend.q5?.toLowerCase() === "no") {
                formDataToSend.q5_attempts = null;
                formDataToSend.q5_lastAttempt = null;
                formDataToSend.q5_changes = null;
            }

            // Q6 logic
            if (formDataToSend.q6?.toLowerCase() === "no") {
                formDataToSend.q6_events = [];
                formDataToSend.q6_other = null;
            }

            // Q7 logic
            if (formDataToSend.q7?.toLowerCase() === "no") {
                formDataToSend.q7_frequency = null;
                formDataToSend.q7_duration = null;
                formDataToSend.q7_intensitySevere = null;
                formDataToSend.q7_intensityWeek = null;
            }

            // Q8 logic
            if (formDataToSend.q8?.toLowerCase() === "no") {
                formDataToSend.q8_method = null;
                formDataToSend.q8_access = null;
                formDataToSend.q8_location = null;
                formDataToSend.q8_preparations = null;
                formDataToSend.q8_likelihood = null;
            }

            // Q10 logic
            if (formDataToSend.q10?.toLowerCase() === "no") {
                formDataToSend.q10_people = null;
                formDataToSend.q10_contact = null;
            }

            const presentationArray = formDataToSend.presentation || [];
            const hasOtherOption = presentationArray.some(item =>
                item === "Other" || item === "other"
            );

            if (!hasOtherOption) {
                formDataToSend.presentation_other = null;
            }

            const payload = mapToSnakeCase(formDataToSend);

            await dispatch(
                updateSuicideDetail({ case_id: caseId, suicideData: payload })
            ).unwrap();

            Swal.fire("Updated!", "Suicide Risk Assessment updated.", "success");

            // Stay on edit page
        } catch (error) {
            Swal.fire("Error", "Failed to update assessment", "error");
        }
    };

    if (!formData) return null;

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/optional/suicide-detail/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>

                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <SuicideDetailForm
                                formData={formData}
                                handleChange={handleChange}
                            />

                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end gap-2">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditSuicideDetail;
